import type {
  IamAdministrationCatalogDto,
  IamAdministrationGroupRowDto,
  IamAdministrationPolicyBindingRowDto,
  IamAdministrationRoleRowDto,
  IamAdministrationUserDetailDto,
  IamAdministrationUserSummaryDto,
} from '@act/shared/data-transfer-objects';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { iamRequests } from 'redux/requests/iam.requests';
import { LoadingStatus } from 'redux/types/sharedTypes';
import { createApiThunk } from 'redux/utils/apiThunk';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';
import { ensureVerifyUser } from 'redux/thunks/referenceData.thunk';
import { memberSlice } from 'redux/slices/memberSlice.slice';

function summaryFromDetail(
  d: IamAdministrationUserDetailDto
): IamAdministrationUserSummaryDto {
  return {
    principalId: d.principalId,
    userId: d.userId,
    firstName: d.firstName,
    lastName: d.lastName,
    displayName: d.displayName,
    email: d.email,
    groups: d.groups,
  };
}

export const fetchIamUsers = createApiThunk(iamRequests.listUsers);
export const fetchIamCatalog = createApiThunk(iamRequests.getCatalog);
export const fetchIamUserDetail = createApiThunk(iamRequests.getUserDetail);
export const fetchIamGroups = createApiThunk(iamRequests.listGroups);
export const fetchIamPolicies = createApiThunk(iamRequests.listPolicies);
export const iamCreatePolicyBinding = createApiThunk(
  iamRequests.createPolicyBinding
);
export const iamDeletePolicyBinding = createApiThunk(
  iamRequests.deletePolicyBinding
);
export const iamCreateGroup = createApiThunk(iamRequests.createGroup);
export const iamDeleteGroup = createApiThunk(iamRequests.deleteGroup);
export const fetchIamRoles = createApiThunk(iamRequests.listRoles);
export const fetchIamPermissions = createApiThunk(iamRequests.listPermissions);
export const fetchIamRolePermissions = createApiThunk(
  iamRequests.getRolePermissions
);
export const iamAddRolePermission = createApiThunk(
  iamRequests.addRolePermission
);
export const iamRemoveRolePermission = createApiThunk(
  iamRequests.removeRolePermission
);
export const iamBulkAddUsersToGroup = createApiThunk(
  iamRequests.bulkAddUsersToGroup
);
export const iamBulkRemoveUsersFromGroup = createApiThunk(
  iamRequests.bulkRemoveUsersFromGroup
);
export const iamCreateRole = createApiThunk(iamRequests.createRole);
export const iamPatchRole = createApiThunk(iamRequests.patchRole);
export const iamDeleteRole = createApiThunk(iamRequests.deleteRole);
export const iamAddUserGroup = createApiThunk(iamRequests.addUserGroup);
export const iamRemoveUserGroup = createApiThunk(iamRequests.removeUserGroup);
export const iamAddPlatformBinding = createApiThunk(
  iamRequests.addPlatformBinding
);
export const iamRemovePlatformBinding = createApiThunk(
  iamRequests.removePlatformBinding
);

export interface IamState {
  users: IamAdministrationUserSummaryDto[];
  usersStatus: LoadingStatus;
  groups: IamAdministrationGroupRowDto[];
  groupsStatus: LoadingStatus;
  roles: IamAdministrationRoleRowDto[];
  rolesStatus: LoadingStatus;
  catalogStatus: LoadingStatus;
  catalog: IamAdministrationCatalogDto | null;
  selectedPrincipalId: string | null;
  userDetail: IamAdministrationUserDetailDto | null;
  userDetailStatus: LoadingStatus;
  policies: IamAdministrationPolicyBindingRowDto[];
  policiesStatus: LoadingStatus;
}

const initialState: IamState = {
  users: [],
  usersStatus: 'initial',
  groups: [],
  groupsStatus: 'initial',
  roles: [],
  rolesStatus: 'initial',
  catalogStatus: 'initial',
  catalog: null,
  selectedPrincipalId: null,
  userDetail: null,
  userDetailStatus: 'initial',
  policies: [],
  policiesStatus: 'initial',
};

export const iamSlice = createSlice({
  name: 'iam',
  initialState,
  reducers: {
    openIamUserDrawer: (state, action: PayloadAction<string>) => {
      state.selectedPrincipalId = action.payload;
      state.userDetailStatus = 'loading';
      state.userDetail = null;
    },
    closeIamUserDrawer: state => {
      state.selectedPrincipalId = null;
      state.userDetail = null;
      state.userDetailStatus = 'initial';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchIamUsers.pending, state => {
        // Stale-while-revalidate: avoid blanking the IAM tables on websocket-driven refetch.
        if (state.usersStatus !== 'success') {
          state.usersStatus = 'loading';
        }
      })
      .addCase(fetchIamUsers.fulfilled, (state, action) => {
        state.usersStatus = 'success';
        state.users = action.payload;
      })
      .addCase(fetchIamUsers.rejected, state => {
        state.usersStatus = 'failed';
      })
      .addCase(fetchIamCatalog.pending, state => {
        state.catalogStatus = 'loading';
      })
      .addCase(fetchIamCatalog.fulfilled, (state, action) => {
        state.catalogStatus = 'success';
        state.catalog = action.payload;
      })
      .addCase(fetchIamCatalog.rejected, state => {
        state.catalogStatus = 'failed';
      })
      .addCase(fetchIamGroups.pending, state => {
        state.groupsStatus = 'loading';
      })
      .addCase(fetchIamGroups.fulfilled, (state, action) => {
        state.groupsStatus = 'success';
        state.groups = action.payload;
      })
      .addCase(fetchIamGroups.rejected, state => {
        state.groupsStatus = 'failed';
      })
      .addCase(fetchIamRoles.pending, state => {
        state.rolesStatus = 'loading';
      })
      .addCase(fetchIamRoles.fulfilled, (state, action) => {
        state.rolesStatus = 'success';
        state.roles = action.payload;
      })
      .addCase(fetchIamRoles.rejected, state => {
        state.rolesStatus = 'failed';
      })
      .addCase(fetchIamPolicies.pending, state => {
        if (state.policiesStatus !== 'success') {
          state.policiesStatus = 'loading';
        }
      })
      .addCase(fetchIamPolicies.fulfilled, (state, action) => {
        state.policiesStatus = 'success';
        state.policies = action.payload;
      })
      .addCase(fetchIamPolicies.rejected, state => {
        state.policiesStatus = 'failed';
      })
      .addCase(fetchIamUserDetail.pending, (state, action) => {
        const pid = action.meta.arg?.params?.principalId;
        if (
          pid &&
          state.userDetail &&
          state.userDetail.principalId === pid
        ) {
          return;
        }
        state.userDetailStatus = 'loading';
      })
      .addCase(fetchIamUserDetail.fulfilled, (state, action) => {
        state.userDetailStatus = 'success';
        state.userDetail = action.payload;
        const idx = state.users.findIndex(
          u => u.principalId === action.payload.principalId
        );
        const row = summaryFromDetail(action.payload);
        if (idx >= 0) {
          state.users[idx] = row;
        }
      })
      .addCase(fetchIamUserDetail.rejected, state => {
        state.userDetailStatus = 'failed';
      })
      .addCase(iamAddUserGroup.fulfilled, (state, action) => {
        state.userDetail = action.payload;
        state.userDetailStatus = 'success';
        const idx = state.users.findIndex(
          u => u.principalId === action.payload.principalId
        );
        const row = summaryFromDetail(action.payload);
        if (idx >= 0) {
          state.users[idx] = row;
        }
      })
      .addCase(iamRemoveUserGroup.fulfilled, (state, action) => {
        state.userDetail = action.payload;
        state.userDetailStatus = 'success';
        const idx = state.users.findIndex(
          u => u.principalId === action.payload.principalId
        );
        const row = summaryFromDetail(action.payload);
        if (idx >= 0) {
          state.users[idx] = row;
        }
      })
      .addCase(iamAddPlatformBinding.fulfilled, (state, action) => {
        state.userDetail = action.payload;
        state.userDetailStatus = 'success';
        const idx = state.users.findIndex(
          u => u.principalId === action.payload.principalId
        );
        const row = summaryFromDetail(action.payload);
        if (idx >= 0) {
          state.users[idx] = row;
        }
      })
      .addCase(iamRemovePlatformBinding.fulfilled, (state, action) => {
        state.userDetail = action.payload;
        state.userDetailStatus = 'success';
        const idx = state.users.findIndex(
          u => u.principalId === action.payload.principalId
        );
        const row = summaryFromDetail(action.payload);
        if (idx >= 0) {
          state.users[idx] = row;
        }
      });
  },
});

export const { openIamUserDrawer, closeIamUserDrawer } = iamSlice.actions;

export const iamReducer = iamSlice.reducer;

/** Narrow root shape so this slice does not import `store-hooks` / `reemaStore` (avoids circular deps with lazy routes). */
type IamRootSlice = { iam: IamState };

const selectIam = (state: IamRootSlice) => state.iam;

export const selectIamUsers = (state: IamRootSlice) => selectIam(state).users;
export const selectIamUsersStatus = (state: IamRootSlice) =>
  selectIam(state).usersStatus;
export const selectIamCatalog = (state: IamRootSlice) =>
  selectIam(state).catalog;
export const selectIamCatalogStatus = (state: IamRootSlice) =>
  selectIam(state).catalogStatus;
export const selectIamGroups = (state: IamRootSlice) => selectIam(state).groups;
export const selectIamGroupsStatus = (state: IamRootSlice) =>
  selectIam(state).groupsStatus;
export const selectIamRoles = (state: IamRootSlice) => selectIam(state).roles;
export const selectIamRolesStatus = (state: IamRootSlice) =>
  selectIam(state).rolesStatus;
export const selectIamSelectedPrincipalId = (state: IamRootSlice) =>
  selectIam(state).selectedPrincipalId;
export const selectIamUserDetail = (state: IamRootSlice) =>
  selectIam(state).userDetail;
export const selectIamUserDetailStatus = (state: IamRootSlice) =>
  selectIam(state).userDetailStatus;
export const selectIamPolicies = (state: IamRootSlice) =>
  selectIam(state).policies;
export const selectIamPoliciesStatus = (state: IamRootSlice) =>
  selectIam(state).policiesStatus;

addToEffectsMiddleware(addEffect => {
  addEffect({
    actions: [
      iamAddUserGroup.fulfilled,
      iamRemoveUserGroup.fulfilled,
    ],
    effect: ({ dispatch }) => {
      void dispatch(fetchIamGroups({}));
    },
  });
  addEffect({
    actions: [
      iamCreateGroup.fulfilled,
      iamDeleteGroup.fulfilled,
      iamCreateRole.fulfilled,
      iamPatchRole.fulfilled,
      iamDeleteRole.fulfilled,
    ],
    effect: ({ dispatch }) => {
      void dispatch(fetchIamCatalog({}));
    },
  });
  addEffect({
    actions: [iamCreateGroup.fulfilled, iamDeleteGroup.fulfilled],
    effect: ({ dispatch }) => {
      void dispatch(fetchIamGroups({}));
    },
  });
  addEffect({
    actions: [
      iamBulkAddUsersToGroup.fulfilled,
      iamBulkRemoveUsersFromGroup.fulfilled,
    ],
    effect: ({ dispatch }) => {
      void dispatch(fetchIamGroups({}));
      void dispatch(fetchIamUsers({}));
    },
  });
  addEffect({
    actions: [
      iamCreateRole.fulfilled,
      iamPatchRole.fulfilled,
      iamDeleteRole.fulfilled,
    ],
    effect: ({ dispatch }) => {
      void dispatch(fetchIamRoles({}));
    },
  });
  addEffect({
    actions: [
      iamCreatePolicyBinding.fulfilled,
      iamDeletePolicyBinding.fulfilled,
      iamAddPlatformBinding.fulfilled,
      iamRemovePlatformBinding.fulfilled,
    ],
    effect: ({ dispatch }) => {
      void dispatch(fetchIamPolicies({}));
    },
  });
  addEffect({
    action: memberSlice.actions.userUpdated,
    effect: ({ dispatch, action, state }) => {
      const userId = action.payload.user.id;
      const { iam } = state;

      if (
        iam.userDetail?.userId === userId &&
        iam.selectedPrincipalId != null
      ) {
        void dispatch(
          fetchIamUserDetail({
            params: { principalId: iam.selectedPrincipalId },
          })
        );
      }

      if (iam.users.some(u => u.userId === userId)) {
        void dispatch(fetchIamUsers({}));
      }

      if (state.actingUser.user?.id === userId) {
        void dispatch(ensureVerifyUser({ force: true }));
      }
    },
  });
});
